import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaArrowRight, FaArrowLeft, FaTimes } from 'react-icons/fa';
import { useOverlay } from '../../contexts/FocusContext';
import { getFullscreenPortalTarget } from './fullscreenPortal';
import './OnboardingTour.css'; // Reuse the same styles

const STORAGE_KEY = 'screenshare_tutorial_completed';

interface TourStep {
  id: string;
  targetId: string | null; // null for no spotlight (welcome/finish steps)
  title: string;
  description: string;
}

const tourSteps: TourStep[] = [
  {
    id: 'welcome',
    targetId: null,
    title: 'Screen Sharing Enabled!',
    description: 'You now have permission to share your screen with everyone in the room.',
  },
  {
    id: 'quality-selector',
    targetId: 'tour-screenshare-quality',
    title: 'Choose Quality',
    description: 'Select your preferred quality. Higher quality looks better but uses more bandwidth. Lower settings work better on slower connections.',
  },
  {
    id: 'share-button',
    targetId: 'tour-screenshare-button',
    title: 'Start Sharing',
    description: 'Click this button to start sharing your screen. You can share a specific window or your entire screen.',
  },
  {
    id: 'finish',
    targetId: null,
    title: "You're Ready to Share!",
    description: 'Select your quality, click Share Screen, and present to everyone. You can stop sharing anytime by clicking the button again.',
  },
];

interface ScreenShareTourProps {
  isVisible: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

interface SpotlightPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

const ScreenShareTour: React.FC<ScreenShareTourProps> = ({ isVisible, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightPosition, setSpotlightPosition] = useState<SpotlightPosition | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [portalTarget, setPortalTarget] = useState(getFullscreenPortalTarget);

  const step = tourSteps[currentStep];

  // Register as overlay when visible - FocusContext handles Unity capture
  useOverlay('screenshare-tour', isVisible, { modal: true });

  // Update spotlight position only (tooltip stays centered)
  const updateSpotlight = useCallback(() => {
    if (!step || !step.targetId) {
      setSpotlightPosition(null);
      return;
    }

    const targetElement = document.querySelector(`[data-tour-id="${step.targetId}"]`);
    if (!targetElement) {
      setSpotlightPosition(null);
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    const padding = 8;

    setSpotlightPosition({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });
  }, [step]);

  // Escape the dock's blur/scroll containing block and stay inside the active
  // fullscreen root. Keep viewport-based spotlight coordinates in sync too.
  useLayoutEffect(() => {
    if (!isVisible) return;

    const update = () => {
      setPortalTarget(getFullscreenPortalTarget());
      updateSpotlight();
    };
    update();

    const fullscreenEvents = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'];
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    fullscreenEvents.forEach((event) => document.addEventListener(event, update));
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      fullscreenEvents.forEach((event) => document.removeEventListener(event, update));
    };
  }, [isVisible, updateSpotlight]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handleBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, currentStep]);

  const handleNext = () => {
    if (isAnimating) return;

    if (currentStep < tourSteps.length - 1) {
      setIsAnimating(true);
      setCurrentStep(currentStep + 1);
      setTimeout(() => setIsAnimating(false), 300);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (isAnimating) return;

    if (currentStep > 0) {
      setIsAnimating(true);
      setCurrentStep(currentStep - 1);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setCurrentStep(0);
    onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setCurrentStep(0);
    onSkip();
  };

  if (!isVisible) return null;

  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;

  return createPortal(
    <div className="onboarding-tour-overlay" style={{ zIndex: 100000 }} role="dialog" aria-modal="true" aria-labelledby="screenshare-tour-title" aria-describedby="screenshare-tour-description">
      {/* Dark overlay with spotlight cutout */}
      <div className="onboarding-backdrop">
        {spotlightPosition && (
          <div
            className="onboarding-spotlight"
            style={{
              top: spotlightPosition.top,
              left: spotlightPosition.left,
              width: spotlightPosition.width,
              height: spotlightPosition.height,
            }}
          />
        )}
      </div>

      {/* Tooltip card - always centered */}
      <div className="onboarding-tooltip-container">
        <div className="onboarding-tooltip">
          {/* Skip button */}
          <button
            className="onboarding-skip-button"
            onClick={handleSkip}
            onMouseDown={(e) => e.preventDefault()}
            title="Skip tutorial"
          >
            <FaTimes size={14} />
          </button>

          {/* Content */}
          <h3 id="screenshare-tour-title" className="onboarding-title">{step.title}</h3>
          <p id="screenshare-tour-description" className="onboarding-description">{step.description}</p>

          {/* Navigation */}
          <div className="onboarding-navigation">
            <button
              className="onboarding-nav-button onboarding-back-button"
              onClick={handleBack}
              onMouseDown={(e) => e.preventDefault()}
              disabled={isFirstStep}
              style={{ visibility: isFirstStep ? 'hidden' : 'visible' }}
            >
              <FaArrowLeft size={12} />
              Back
            </button>

            {/* Step indicators */}
            <div className="onboarding-dots">
              {tourSteps.map((_, index) => (
                <span
                  key={index}
                  className={`onboarding-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                />
              ))}
            </div>

            <button
              className="onboarding-nav-button onboarding-next-button"
              onClick={handleNext}
              onMouseDown={(e) => e.preventDefault()}
            >
              {isLastStep ? 'Finish' : 'Next'}
              {!isLastStep && <FaArrowRight size={12} />}
            </button>
          </div>
        </div>
      </div>
    </div>,
    portalTarget
  );
};

// Helper function to check if screen share tutorial has been completed
export const hasCompletedScreenShareTutorial = (): boolean => {
  return localStorage.getItem(STORAGE_KEY) === 'true';
};

// Helper function to reset screen share tutorial (for replay)
export const resetScreenShareTutorial = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

export default ScreenShareTour;
